import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, InputSignal } from '@angular/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { EditedPoint, EditedStatus, GetSuggestedPointsBody } from '../../../../interfaces/edit-route.interface';
import { EditRouteService } from '../../../../services/edit-route.service';
import { SuggestedPointsService } from '../../../suggested-points/services/suggested-points.service';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { SuggestedPointsComponent } from '../../../suggested-points/suggested-points.component';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { BottomSheetConfig, MatBottomSheetAddNewPointOrReplacePointData } from 'src/app/interfaces/mat.interface';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { StandardDialog } from 'src/app/shared/dialogs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-add-new-point-card',
  standalone: true,
  imports: [],
  templateUrl: './add-new-point-card.component.html',
  styleUrls: ['./add-new-point-card.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AddNewPointCardComponent {
  public previousEditedPoint: InputSignal<EditedPoint | null> = input.required<EditedPoint | null>();
  public nextEditedPoint: InputSignal<EditedPoint | null> = input.required<EditedPoint | null>();
  public orderIndex: InputSignal<number> = input.required<number>();
  
  private _translateService = inject(TranslateService);
  private _customRouteService = inject(CustomRouteService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _editRouteService = inject(EditRouteService);
  private _suggestedPointsService = inject(SuggestedPointsService);
  private _matBottomSheetService = inject(MatBottomSheetService);
  private _matDialogService = inject(MatDialogService);
  private _breakpointsService = inject(BreakpointsService);
  
  private readonly _token  = computed(() => this._customRouteService.token());
  private readonly _customRouteData = computed(() => this._customRouteService.customRouteData());
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  private _isMobile = computed(() => this._breakpointsService.isMobile());
  public isLoadingResourcesByIdOnEmptySpace = computed(() => this._editRouteService.isLoadingResourcesByIdOnEmptySpace() === this.orderIndex());
  
  handleAddNewPointClick() {
    this._editRouteService.setIsLoadingResourcesByIdOnEmptySpace(this.orderIndex());
    const dayToString = dayjs(this._customRouteData().dailyRoutes[this._dayToShowIndex()].date).format('YYYY-MM-DD');

    // calculate startTime
    let startTime: string;
    if (this.previousEditedPoint()) {
      startTime = dayjs(this.previousEditedPoint().endDate).add(15, 'minute').format('HH:mm'); // add 15 min due to transports
    } else {
      startTime = '09:15';
    }
    
    // calculate duration
    let duration: number; // minutes
    if (this.nextEditedPoint()) {
      const dayjsNextEditedPointStartDate = dayjs(this.nextEditedPoint().startDate);
      const dayjsPreviousEditedPointEndDate = this.previousEditedPoint()
        ? dayjs(`${dayToString} ${dayjs(this.previousEditedPoint().endDate).format('HH:mm')}`, 'YYYY-MM-DD HH:mm')
        : dayjs(`${dayToString} 09:00`, 'YYYY-MM-DD HH:mm');
      duration = (dayjsNextEditedPointStartDate).diff(dayjsPreviousEditedPointEndDate, 'minute') - 30; // subtract 30 min due to transports
    } else {
      const lastTime = '24:00'; // 00:00 in 24h format
      duration = dayjs(lastTime, 'HH:mm').diff(dayjs(startTime, 'HH:mm'), 'minute');
    }
    
    const body: GetSuggestedPointsBody = {
      token: this._token(),
      day: dayToString,
      startTime: startTime,
      duration: duration,
      previousPoint: this.previousEditedPoint() && // TODO: HABLAR CON BACKEND COMO GESTIONAMOS CUANDO PREVIOUS LLEGA NULL
        { 
          id: this.previousEditedPoint().id, 
          type: this.previousEditedPoint().type 
        },
      nextPoint: this.nextEditedPoint() &&
        { 
          id: this.nextEditedPoint().id, 
          type: this.nextEditedPoint().type 
        }
    };
    
    this._editRouteService.getSuggestedPoints(body).subscribe({
      next: async(response) => {
        this._suggestedPointsService.suggestedPoints.set(response.points);
        this._suggestedPointsService.interestList.set(response.categoryList);
        await this._openSuggestedPointsBottomSheet();
        this._editRouteService.setIsLoadingResourcesByIdOnEmptySpace(null);
      },
      error: (error: HttpErrorResponse) => {
        if (!environment.production) {
          console.error(`Error fetching suggested points: ${error.error}`);
        }
        this._openNoSuggestedPointsErrorDialog();
        this._editRouteService.setIsLoadingResourcesByIdOnEmptySpace(null);
      }
    })
  }
  
  private async _openSuggestedPointsBottomSheet(): Promise<void> {
    const bottomSheetConfig: BottomSheetConfig<SuggestedPointsComponent, MatBottomSheetAddNewPointOrReplacePointData> = {
      component: SuggestedPointsComponent,
      data: {
        title: this._translateService.instant('Añadir nuevo recurso'),
        dayToShowIndex: this._dayToShowIndex(),
        orderIndex: this.orderIndex(),
        status: EditedStatus.ADDED,
      },
      panelClass: this._isMobile() ? 'suggested-points-bottom-sheet--mobile' : 'suggested-points-bottom-sheet--desktop',
    }
    
    await this._matBottomSheetService.openMatBottomSheet(bottomSheetConfig);
  }
  
  // TODO: WAIT FOR UX/UI CONFIRMATION
  private _openNoSuggestedPointsErrorDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: StandardDialog,
      data: {
        title: this._translateService.instant('Edicion no compatible'),
        content: this._translateService.instant('No hemos encontrado más recursos que se adapten en esta franja horaria'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
}
